import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CoArchitectLogo, ArrowLeft, AlertCircle } from '../components/Icons';
import { API } from '../contexts/AuthContext';

type ReturnStatus = 'success' | 'failed' | 'pending' | 'loading';

const SKU_LABELS: Record<string, string> = {
  pack_small:        'Small Pack',
  pack_medium:       'Medium Pack',
  pack_large:        'Large Pack',
  starter_monthly:   'Starter Monthly',
  starter_annual:    'Starter Annual',
  pro_monthly:       'Pro Monthly',
  pro_annual:        'Pro Annual',
  custom_topup:      'Custom Top-Up',
};

const SKU_CREDITS: Record<string, string> = {
  pack_small:        '20 credits',
  pack_medium:       '60 credits',
  pack_large:        '200 credits',
  starter_monthly:   '50 credits/mo',
  starter_annual:    '50 credits/mo',
  pro_monthly:       '250 credits/mo',
  pro_annual:        '250 credits/mo',
};

const SUBSCRIPTION_SKUS = new Set<string>([
  'starter_monthly', 'starter_annual', 'pro_monthly', 'pro_annual',
]);

function mapDodoStatus(raw: string | undefined): ReturnStatus {
  if (!raw) return 'pending';
  if (raw === 'succeeded' || raw === 'active') return 'success';
  if (raw === 'failed'    || raw === 'cancelled') return 'failed';
  return 'pending';
}

function CheckIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path
        d="M7 18l8 8L29 10"
        stroke="#16A34A"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 40,
          strokeDashoffset: 0,
          animation: 'pr-draw 0.5s 0.3s ease both',
        }}
      />
      <style>{`@keyframes pr-draw { from { stroke-dashoffset: 40 } to { stroke-dashoffset: 0 } }`}</style>
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <path d="M10 10l16 16M26 10L10 26" stroke="#DC2626" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

function PaymentReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ReturnStatus>('loading');

  const sku            = searchParams.get('sku') ?? '';
  const paymentId      = searchParams.get('payment_id');
  const subscriptionId = searchParams.get('subscription_id');
  const creditsParam   = searchParams.get('credits');

  useEffect(() => {
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  useEffect(() => {
    const entityParam = paymentId
      ? `payment_id=${encodeURIComponent(paymentId)}`
      : subscriptionId
        ? `subscription_id=${encodeURIComponent(subscriptionId)}`
        : null;

    if (!entityParam) {
      setStatus('pending');
      return;
    }

    axios
      .get<{ status: string }>(`${API}/payments/verify?${entityParam}`)
      .then(res => setStatus(mapDodoStatus(res.data.status)))
      .catch(() => setStatus('pending'));
  }, [paymentId, subscriptionId]);

  const skuLabel = SKU_LABELS[sku] ?? sku;
  const creditsLabel = sku === 'custom_topup' && creditsParam
    ? `${creditsParam} credits`
    : SKU_CREDITS[sku];

  const isSubscription = SUBSCRIPTION_SKUS.has(sku);

  return (
    <div className="page-root">
      <header className="app-header">
        <div className="header-inner">
          <div className="logo-lockup">
            <div className="logo-mark-sm"><CoArchitectLogo size={28} /></div>
            <span className="heading-brand logo-text-sm">PAYMENT</span>
          </div>
          <div className="header-actions-row">
            <button
              className="header-action-btn"
              onClick={() => navigate('/credits')}
              aria-label="Back to credits"
            >
              <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="page-scroll">
        <div className="page-container" style={{ paddingTop: 24, paddingBottom: 48 }}>

          <div className="payment-return-card" style={{ margin: '0 auto', maxWidth: 420 }}>

            {status === 'loading' ? (
              <>
                <div className="payment-return-icon pending">
                  <div className="spinner" style={{ width: 32, height: 32 }} aria-hidden="true" />
                </div>
                <h1 className="payment-return-heading">Verifying Payment…</h1>
                <p className="payment-return-sub">Checking your payment status with our processor.</p>
              </>
            ) : (
              <>
                <div className={`payment-return-icon ${status}`}>
                  {status === 'success' && <CheckIcon />}
                  {status === 'failed'  && <XIcon />}
                  {status === 'pending' && (
                    <div className="spinner" style={{ width: 32, height: 32 }} aria-hidden="true" />
                  )}
                </div>

                <h1 className="payment-return-heading">
                  {status === 'success' && 'Payment Successful!'}
                  {status === 'failed'  && "Payment Didn't Go Through"}
                  {status === 'pending' && 'Verifying Payment…'}
                </h1>

                {(status === 'success' || status === 'pending') && skuLabel && (
                  <div className="payment-return-product">
                    <span className="payment-return-sku-badge">{skuLabel}</span>
                    {creditsLabel && (
                      <span className="payment-return-credits-badge">
                        {creditsLabel}{isSubscription ? ' per month' : ''}
                      </span>
                    )}
                  </div>
                )}

                <p className="payment-return-sub">
                  {status === 'success' &&
                    'Your credits are being added — this usually takes just a few seconds.'}
                  {status === 'failed' && (
                    <>Nothing was charged. You can try again or choose a different payment method.</>
                  )}
                  {status === 'pending' &&
                    'Hang tight while we confirm your payment with our processor.'}
                </p>

                {status === 'failed' && (
                  <div className="alert alert-error" role="alert" style={{ marginBottom: 24 }}>
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    <span>Your payment could not be processed.</span>
                  </div>
                )}

                <div className="payment-return-actions">
                  {status === 'success' && (
                    <>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => navigate('/credits')}
                      >
                        View My Credits
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => navigate('/credits')}
                      >
                        Back to Plans
                      </button>
                    </>
                  )}
                  {status === 'failed' && (
                    <>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => navigate('/credits')}
                      >
                        Try Again
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => navigate('/')}
                      >
                        Go Home
                      </button>
                    </>
                  )}
                  {status === 'pending' && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate('/credits')}
                    >
                      Check Credits
                    </button>
                  )}
                </div>
              </>
            )}

          </div>

        </div>
      </div>
    </div>
  );
}

export default PaymentReturn;
